/**
 * src/pages/venue/MorningDigestPage.tsx — 2004va
 *
 * The landing view for picker-mode. This is what your daughter's husband
 * should open at 10am and see "last night, at a glance."
 *
 * Architecture:
 *   - Header stats strip: total / unactioned / needs-approval / posted
 *   - AI picks grid: top N unactioned shots, sorted by ai_score desc
 *   - Coverage gaps callout: AI flags where it found nothing strong
 *     (computed client-side for demo; server-side job later)
 *   - Per-tile action sheet on tap: post, needs approval, save, reject,
 *     override AI approval judgement
 *
 * Signed URLs: uses getSignedUrls from cloudinary-signed.ts for every
 * thumbnail. Cache in the helper means repeat renders don't re-sign.
 *
 * Data flow: react-query fetches picks + stats, mutations are optimistic
 * (UI updates before server confirms) with rollback on error.
 */

import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, Send, ShieldAlert, X, Save, MoreHorizontal, AlertTriangle, Copy } from 'lucide-react'
import {
  fetchDigestPicks,
  fetchDigestStats,
  fetchApprovalQueue,
  updatePickerAction,
  logApprovalDecision,
  type PickerSession,
} from '@/lib/venue'
import { Spinner } from '@/components/ui'
import { cn } from '@/components/ui/utils'
import { ApproverDetailsModal } from './ApproverDetailsModal'
import type { Venue, VenuePick, PickerAction, ApprovalRequirement } from '@/types'

// ── Approval badge colour scheme ──────────────────────────────────────────────

const APPROVAL_BADGE: Record<ApprovalRequirement, { label: string; cls: string; icon: typeof Check }> = {
  not_required: {
    label: 'Clear',
    cls:   'bg-emerald-900/70 text-emerald-300 border-emerald-700/40',
    icon:  Check,
  },
  required: {
    label: 'Approval',
    cls:   'bg-amber-900/70 text-amber-300 border-amber-700/40',
    icon:  ShieldAlert,
  },
  uncertain: {
    label: 'Check',
    cls:   'bg-orange-900/70 text-orange-300 border-orange-700/40',
    icon:  AlertTriangle,
  },
}

// ── Stats strip ───────────────────────────────────────────────────────────────

interface StatsStripProps {
  total:             number
  unactioned:        number
  needsApproval:     number
  posted:            number
  pendingApprovals?: number
  onApprovalsClick?: () => void
}

function StatsStrip({ total, unactioned, needsApproval, posted, pendingApprovals = 0, onApprovalsClick }: StatsStripProps) {
  return (
    <div className="bg-room-surface border border-room-border rounded-2xl p-4">
      <p className="text-[10px] uppercase tracking-widest text-room-muted font-semibold mb-3">
        Last night at a glance
      </p>
      <div className="flex gap-3 items-stretch">
        <Stat label="Shots"      value={total}         tone="default" />
        <Stat label="To review"  value={unactioned}    tone="gold"    />
        <Stat label="Approval"   value={needsApproval} tone="warning" />
        <Stat label="Queued"     value={posted}        tone="accent"  />
        {pendingApprovals > 0 && (
          <button type="button" onClick={onApprovalsClick}
            className="flex flex-col items-center justify-center px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-400/30 active:scale-95 transition-all">
            <span className="text-xl font-black text-amber-400 leading-none">{pendingApprovals}</span>
            <span className="text-[9px] text-amber-400/70 font-semibold uppercase tracking-wider mt-0.5">Pending</span>
          </button>
        )}
      </div>
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone: 'default' | 'gold' | 'warning' | 'accent' }) {
  const toneCls =
    tone === 'gold'    ? 'text-room-gold' :
    tone === 'warning' ? 'text-room-warning' :
    tone === 'accent'  ? 'text-room-accent' :
                         'text-white'
  return (
    <div>
      <p className={cn('text-2xl font-black tracking-tight', toneCls)}>{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-room-muted font-semibold mt-0.5">
        {label}
      </p>
    </div>
  )
}

// ── Pick tile ─────────────────────────────────────────────────────────────────

interface PickTileProps {
  pick:       VenuePick
  signedUrl?: string
  onTap:      () => void
}

function PickTile({ pick, signedUrl, onTap }: PickTileProps) {
  const score         = pick.ai_score ?? pick.media?.ai_route_score ?? 0
  const approvalReq   = pick.media?.approval_requirement ?? 'uncertain'
  const badge         = APPROVAL_BADGE[approvalReq]
  const BadgeIcon     = badge.icon

  // Visual state for already-actioned picks
  const actionedTone =
    pick.picker_action === 'rejected'       ? 'opacity-40 grayscale'  :
    pick.picker_action === 'posted'         ? 'ring-2 ring-room-accent/70' :
    pick.picker_action === 'saved'          ? 'ring-2 ring-white/40'  :
    pick.picker_action === 'needs_approval' ? 'ring-2 ring-room-warning/60' :
    ''

  return (
    <button
      type="button"
      onClick={onTap}
      className={cn(
        'relative aspect-[3/4] bg-room-surface border border-room-border rounded-xl overflow-hidden',
        'active:scale-[0.98] transition-transform text-left',
        actionedTone,
      )}
    >
      {/* Image or placeholder */}
      {signedUrl ? (
        <img
          src={signedUrl}
          alt={pick.media?.ai_description ?? 'shot'}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        // Placeholder gradient — used until signedUrl resolves (or when
        // extractPublicId returns null for a malformed URL)
        <div className="absolute inset-0 bg-gradient-to-br from-room-border to-room-bg" />
      )}

      {/* Dark bottom gradient for legibility */}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/90 to-transparent" />

      {/* Score (top-left) */}
      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-black/70  text-[11px] text-white font-bold font-mono">
        {(score).toFixed(2)}
      </div>

      {/* Approval badge (top-right) */}
      <div className={cn(
        'absolute top-2 right-2 px-2 py-0.5 rounded-md border text-[10px] font-semibold',
        'flex items-center gap-1 ',
        badge.cls,
      )}>
        <BadgeIcon size={10} strokeWidth={2.5} />
        <span className="hidden xs:inline">{badge.label}</span>
      </div>

      {/* Bottom metadata */}
      <div className="absolute inset-x-0 bottom-0 p-2.5 space-y-1">
        {pick.media?.ai_tags && pick.media.ai_tags.length > 0 && (
          <p className="text-[10px] text-white/80 line-clamp-1 font-medium">
            {pick.media.ai_tags.slice(0, 3).join(' · ')}
          </p>
        )}
        {pick.picker_action && (
          <p className="text-[9px] uppercase tracking-widest text-white/90 font-bold">
            {pick.picker_action.replace('_', ' ')}
          </p>
        )}
      </div>
    </button>
  )
}

// ── Action sheet ──────────────────────────────────────────────────────────────

interface ActionSheetProps {
  pick:      VenuePick
  signedUrl: string | undefined   // Cloudinary URL for the image preview
  onClose:   () => void
  onAction:  (action: PickerAction) => void
  onRequestApproval: () => void
}

function ActionSheet({ pick, signedUrl, onClose, onAction, onRequestApproval }: ActionSheetProps) {
  const approvalReq = pick.media?.approval_requirement ?? 'uncertain'
  const reasoning   = pick.media?.approval_reasoning
  const needsApprovalFlow = approvalReq === 'required' || approvalReq === 'uncertain'

  // Full-screen modal layout:
  //   Top half  — the photo, large, with close button and key metadata
  //                overlaid at the bottom of the photo region
  //   Bottom    — the four action buttons, easy reach on a phone
  //
  // This replaces the old bottom-sheet layout which obscured the thumbnail
  // the picker had just tapped on. Pro pickers need to see the photo they're
  // deciding about; that is the whole point of a picture-desk interface.
  return (
    <div className="fixed inset-0 z-40 bg-room-bg flex flex-col animate-fade-in">

      {/* ── Photo region (top half) ─────────────────────────────────── */}
      <div className="relative flex-1 min-h-0 bg-black">
        {signedUrl ? (
          <img
            src={signedUrl}
            alt={pick.media?.ai_description ?? 'shot'}
            className="absolute inset-0 w-full h-full object-contain"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-room-border to-room-bg" />
        )}

        {/* Close button — top-right, high contrast against any image */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-11 h-11 rounded-full bg-black/70  border border-white/10 flex items-center justify-center text-white active:scale-95 transition-transform"
        >
          <X size={20} strokeWidth={2.5} />
        </button>

        {/* Photographer credit — bottom-left of photo area */}
        {pick.media?.photographer_credit && (
          <div className="absolute bottom-3 left-4 px-2.5 py-1 rounded-md bg-black/70 ">
            <p className="text-[11px] text-white italic">
              {pick.media.photographer_credit}
            </p>
          </div>
        )}
      </div>

      {/* ── Actions region (bottom) ─────────────────────────────────── */}
      <div className="bg-room-bg border-t border-room-border p-5 pb-8 space-y-4 max-h-[45vh] overflow-y-auto">

        {/* Description + AI context. Shorter than before; lives below
            the photo not over it. */}
        {pick.media?.ai_description && (
          <p className="text-sm text-white/90 leading-snug">
            {pick.media.ai_description}
          </p>
        )}

        {reasoning?.notes && (
          <p className="text-xs text-room-muted leading-relaxed">
            {reasoning.notes}
          </p>
        )}

        {reasoning?.factors && reasoning.factors.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {reasoning.factors.map((f, i) => (
              <span key={i} className="text-[10px] bg-room-surface border border-room-border rounded-md px-2 py-0.5 text-room-muted">
                {f}
              </span>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="space-y-2 pt-1">

          {needsApprovalFlow ? (
            <>
              <ActionButton
                icon={ShieldAlert}
                label="Request approval"
                tone="warning"
                onClick={() => { onRequestApproval(); onClose() }}
              />
              <ActionButton
                icon={Check}
                label="Override — post without approval"
                tone="ghost"
                onClick={() => { onAction('overridden'); onClose() }}
              />
            </>
          ) : (
            <ActionButton
              icon={Send}
              label="Post"
              tone="accent"
              onClick={() => { onAction('posted'); onClose() }}
            />
          )}

          <ActionButton
            icon={Save}
            label="Save for later"
            tone="default"
            onClick={() => { onAction('saved'); onClose() }}
          />
          <ActionButton
            icon={X}
            label="Reject"
            tone="danger"
            onClick={() => { onAction('rejected'); onClose() }}
          />
        </div>
      </div>
    </div>
  )
}

interface ActionButtonProps {
  icon:    typeof Check
  label:   string
  tone:    'accent' | 'warning' | 'danger' | 'default' | 'ghost'
  onClick: () => void
}

function ActionButton({ icon: Icon, label, tone, onClick }: ActionButtonProps) {
  const toneCls =
    tone === 'accent'  ? 'bg-room-accent/90 hover:bg-room-accent text-white' :
    tone === 'warning' ? 'bg-room-warning/90 hover:bg-room-warning text-black' :
    tone === 'danger'  ? 'bg-room-error/15 hover:bg-room-error/25 text-room-error border border-room-error/40' :
    tone === 'ghost'   ? 'bg-transparent hover:bg-room-surface text-room-muted border border-room-border' :
                         'bg-room-surface hover:bg-white/5 text-white border border-room-border'
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full py-3.5 rounded-xl text-sm font-semibold transition-all min-h-[48px]',
        'flex items-center justify-center gap-2',
        toneCls,
      )}
    >
      <Icon size={16} strokeWidth={2} />
      {label}
    </button>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

interface MorningDigestPageProps {
  venue:  Venue
  picker: PickerSession
}

export function MorningDigestPage({ venue, picker }: MorningDigestPageProps) {
  const qc = useQueryClient()
  const [selectedPickId,   setSelectedPickId]   = useState<string | null>(null)
  const [approvingPickId,  setApprovingPickId]  = useState<string | null>(null)
  const [toastUrl,         setToastUrl]         = useState<string | null>(null)

  // Fetch picks + stats
  const { data: stats } = useQuery({
    queryKey: ['venue-digest-stats', venue.id],
    queryFn:  () => fetchDigestStats({ venue_id: venue.id }),
  })

  const { data: approvalQueue } = useQuery({
    queryKey: ['venue-approval-queue', venue.id],
    queryFn:  () => fetchApprovalQueue(venue.id),
    staleTime: 5 * 60_000,
  })
  const pendingApprovals = approvalQueue?.filter(q => q.request.status === 'pending').length ?? 0

  const { data: picks, isLoading: picksLoading, error: picksError } = useQuery({
    queryKey: ['venue-digest-picks', venue.id],
    queryFn:  () => fetchDigestPicks({ venue_id: venue.id, limit: 50 }),
  })

  // Direct URL map — Cloudinary URLs are public, no signing needed
  const signedMap = useMemo(() => {
    if (!picks) return {} as Record<string, string>
    const map: Record<string, string> = {}
    picks.forEach(p => {
      const url = p.media?.cloudinary_url
      if (url) map[p.media_item_id] = url.replace('/upload/', '/upload/w_600,c_fill,q_auto,f_auto/')
    })
    return map
  }, [picks])


  // Mutation: picker action
  const actionMutation = useMutation({
    mutationFn: async (vars: { pickId: string; action: PickerAction }) => {
      await updatePickerAction({
        pick_id:   vars.pickId,
        picker_id: picker.picker_id,
        action:    vars.action,
      })
      if (vars.action === 'overridden') {
        await logApprovalDecision({
          pick_id:   vars.pickId,
          picker_id: picker.picker_id,
          decision:  'override_not_required',
          reason:    'Picker override — no approval deemed necessary',
        })
      }
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ['venue-digest-picks', venue.id] })
      const previous = qc.getQueryData<VenuePick[]>(['venue-digest-picks', venue.id])
      qc.setQueryData<VenuePick[]>(['venue-digest-picks', venue.id], old =>
        (old ?? []).map(p =>
          p.id === vars.pickId
            ? { ...p, picker_action: vars.action, actioned_at: new Date().toISOString() }
            : p
        )
      )
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['venue-digest-picks', venue.id], ctx.previous)
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['venue-digest-stats', venue.id] })
    },
  })

  const selectedPick = useMemo(
    () => picks?.find(p => p.id === selectedPickId) ?? null,
    [picks, selectedPickId]
  )

  // Coverage gap computation — naive client-side heuristic for demo.
  // Real version: server job that knows the venue's event schedule.
  const coverageGaps = useMemo(() => {
    if (!picks || picks.length === 0) return [] as string[]
    const gaps: string[] = []
    const strongCount = picks.filter(p => (p.ai_score ?? 0) >= 0.75).length
    if (strongCount < 5) {
      gaps.push('Few strong shots tonight — consider re-running aesthetic calibration')
    }
    const actCount = picks.filter(p =>
      p.media?.ai_tags?.some(t => /\b(stage|dj|booth|performer|act)\b/i.test(t))
    ).length
    if (actCount < 3) {
      gaps.push('Thin coverage of the main act / stage')
    }
    return gaps
  }, [picks])

  // ── Render ─────────────────────────────────────────────────────────────────

  if (picksLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size={24} />
      </div>
    )
  }

  if (picksError) {
    return (
      <div className="bg-room-error/15 border border-room-error/40 rounded-2xl p-5 text-sm text-room-error">
        Could not load tonight&rsquo;s digest.
        {picksError instanceof Error && <span className="block mt-1 text-xs opacity-75">{picksError.message}</span>}
      </div>
    )
  }

  const [showApprovals, setShowApprovals] = useState(false)

  return (
    <div className="space-y-5">

      <StatsStrip
        total={stats?.total ?? 0}
        unactioned={stats?.unactioned ?? 0}
        needsApproval={stats?.needs_approval ?? 0}
        posted={stats?.posted ?? 0}
        pendingApprovals={pendingApprovals}
        onApprovalsClick={() => setShowApprovals(v => !v)}
      />

      {/* Inline approvals drawer — collapsed by default */}
      {showApprovals && (
        <div className="rounded-2xl border border-room-warning/30 bg-room-warning/5 p-4">
          <p className="text-room-warning text-xs font-semibold uppercase tracking-wider mb-3">
            {pendingApprovals > 0 ? `${pendingApprovals} pending approval${pendingApprovals > 1 ? 's' : ''}` : 'No pending approvals'}
          </p>
          {approvalQueue && approvalQueue.length > 0
            ? approvalQueue.filter(q => q.request.status === 'pending').map(({ request }) => (
                <div key={request.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div>
                    <p className="text-white text-sm font-medium">{request.approver_name}</p>
                    <p className="text-white/40 text-xs">{request.approver_contact} · sent {new Date(request.created_at).toLocaleTimeString()}</p>
                  </div>
                  <button type="button" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/approve/${request.access_token}`) }}
                    className="text-xs text-room-warning border border-room-warning/30 rounded-lg px-2 py-1 hover:bg-room-warning/10 transition-colors">
                    Copy link
                  </button>
                </div>
              ))
            : <p className="text-white/40 text-sm">No pending approvals</p>
          }
        </div>
      )}

      {/* Coverage gaps */}
      {coverageGaps.length > 0 && (
        <div className="bg-room-warning/10 border border-room-warning/30 rounded-2xl p-4 space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-room-warning font-bold flex items-center gap-1.5">
            <AlertTriangle size={12} />
            Coverage gaps
          </p>
          <ul className="space-y-1.5">
            {coverageGaps.map((gap, i) => (
              <li key={i} className="text-sm text-white/90 leading-snug">{gap}</li>
            ))}
          </ul>
        </div>
      )}

      {/* AI picks header */}
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-lg font-bold text-white">AI picks</h2>
          <p className="text-xs text-room-muted mt-0.5">
            {picks?.length ?? 0} shots, highest scored first
          </p>
        </div>
        <button
          type="button"
          className="text-xs text-room-muted hover:text-white flex items-center gap-1"
          aria-label="Filter picks"
        >
          <MoreHorizontal size={16} />
        </button>
      </div>

      {/* Picks grid */}
      {!picks || picks.length === 0 ? (
        <div className="bg-room-surface border border-room-border rounded-2xl p-8 text-center">
          <p className="text-white font-semibold mb-1">Nothing to review yet</p>
          <p className="text-sm text-room-muted">
            Shots from last night will appear here once processed.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {picks.map(pick => (
            <PickTile
              key={pick.id}
              pick={pick}
              signedUrl={signedMap?.[pick.media_item_id]}
              onTap={() => setSelectedPickId(pick.id)}
            />
          ))}
        </div>
      )}

      {/* Action sheet */}
      {selectedPick && (
        <ActionSheet
          pick={selectedPick}
          signedUrl={signedMap?.[selectedPick.media_item_id]}
          onClose={() => setSelectedPickId(null)}
          onAction={action => {
            actionMutation.mutate({ pickId: selectedPick.id, action })
          }}
          onRequestApproval={() => {
            // Open the approver-details modal to collect name, contact,
            // relationship, channel. createApprovalRequest is called inside
            // the modal on submit.
            setApprovingPickId(selectedPick.id)
          }}
        />
      )}

      {/* Approver details modal */}
      {approvingPickId && (() => {
        const pick = picks?.find(p => p.id === approvingPickId)
        if (!pick) return null
        return (
          <ApproverDetailsModal
            pick={pick}
            picker={picker}
            onClose={() => setApprovingPickId(null)}
            onComplete={(approverUrl) => {
              setToastUrl(approverUrl)
              qc.invalidateQueries({ queryKey: ['venue-digest-picks', venue.id] })
              qc.invalidateQueries({ queryKey: ['venue-digest-stats', venue.id] })
              qc.invalidateQueries({ queryKey: ['venue-approval-queue', venue.id] })
            }}
          />
        )
      })()}

      {/* Link toast — in production this would be replaced by the notify
          edge function sending the message. For demo we show the URL so
          the picker can copy it and forward manually. */}
      {toastUrl && (
        <div
          className="fixed bottom-24 inset-x-4 z-50 max-w-lg mx-auto animate-slide-up"
          onClick={() => setToastUrl(null)}
        >
          <div className="bg-room-warning/95 border border-room-warning rounded-2xl p-4 shadow-2xl text-black">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest font-bold mb-1">
                  Approval link created
                </p>
                <p className="text-xs font-mono truncate">{toastUrl}</p>
                <p className="text-[11px] mt-1 opacity-80">
                  Tap to copy. In production this is sent via the chosen channel.
                </p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  navigator.clipboard?.writeText(toastUrl).catch(() => {})
                  setToastUrl(null)
                }}
                className="flex-shrink-0 p-2 rounded-lg bg-black/15 hover:bg-black/25 transition-colors"
                aria-label="Copy link"
              >
                <Copy size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
